<template>
  <div class="bg-white flex justify-center">
    <div class="container">
      <DynamicZone v-if="page" :blocks="blocks" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DynamicZone from "~/components/DynamicZone.vue";

const route = useRoute();
const strapi = useStrapiService();
const page = ref<any>(null);
const blocks = ref<any[]>([]);
let slug: any;

const updatePageTitle = (pageSlug: string) => {
  let title = "OnePass mobile";
  if (pageSlug === "catch-connect-rebrand-faqs") {
    title = "Catch Connect is now OnePass mobile";
  }
  useHead({
    title,
  });
};

try {
  // Get the slug from route params and properly decode URL-encoded characters
  // This fixes WebView navigation where slashes get encoded as %2F
  slug = route.params.slug as string;
  
  // IMPORTANT: Skip CMS lookup for known frontend routes
  // This prevents WebView from trying to load CMS content for Vue pages
  const knownFrontendRoutes = [
    'account/verification',
    'account/dashboard',
    'account/biometrics',
    'cart',
    'checkout',
    'sim-activation',
    'forget-password',
    'recharge'
  ];
  
  // Check if this is a known frontend route (normalize the slug first)
  const normalizedSlug = slug?.toLowerCase().replace(/^\/+/, '').replace(/%2f/gi, '/');
  if (knownFrontendRoutes.some(route => normalizedSlug?.startsWith(route))) {
    console.log(`[Pages] Detected frontend route: ${normalizedSlug}, skipping CMS lookup`);
    throw createError({
      statusCode: 404,
      statusMessage: `Not a CMS page: ${slug}`,
      fatal: true
    });
  }
  
  // Check if the slug is URL-encoded (starts with %2F for example)
  if (slug?.includes('%2F') || slug?.includes('%2f')) {
    console.log(`[Pages] Detected URL-encoded slug: ${slug}, decoding...`);
    slug = decodeURIComponent(slug);
    console.log(`[Pages] Decoded slug: ${slug}`);
  }
  
  // Handle double-encoded slashes that can happen in some WebView environments
  // For example, /%252Faccount becomes /account and /%252Faccount%252Fdashboard becomes /account/dashboard
  if (slug?.includes('%25')) {
    console.log(`[Pages] Detected double-encoded slug: ${slug}, decoding again...`);
    slug = decodeURIComponent(slug);
    console.log(`[Pages] Decoded slug (second pass): ${slug}`);
  }
  
  // Remove any leading slash to maintain consistency with Strapi lookups
  if (slug?.startsWith('/')) {
    slug = slug.substring(1);
  }
  
  console.log(`[Pages] Final slug for Strapi lookup: ${slug}`);
  page.value = await strapi.getPageBySlug(slug);
  blocks.value = page.value?.components || [];
  updatePageTitle(slug);
} catch (error) {
  // Enhanced error logging to help diagnose WebView navigation issues
  console.error(`[Pages] Error loading page with slug: ${slug}`, error);
  
  // Check if we're running in a WebView environment
  const isWebView = process.client && (
    navigator.userAgent.includes('WebView') ||
    navigator.userAgent.includes('wv') ||
    // Android pattern
    (navigator.userAgent.includes('Android') && navigator.userAgent.includes('Version/'))
  );
  
  if (isWebView) {
    console.warn(`[Pages] ⚠️ WebView environment detected, navigation issues are common`);
    console.warn(`[Pages] User agent: ${navigator.userAgent}`);
    console.warn(`[Pages] Current URL: ${window.location.href}`);
    console.warn(`[Pages] Attempted slug: ${slug}`);
  }
  
  // Include more descriptive error info
  throw createError({
    statusCode: 404,
    statusMessage: `Page not found: ${slug}`,
    fatal: true,
    cause: error
  });
}

// Scroll to top when component is mounted
onMounted(() => {
  console.log("onMounted - attempting scroll");

  // Try multiple approaches to ensure scrolling works
  // 1. Immediate scroll attempt
  window.scrollTo(0, 0);

  // 2. Force scroll with setTimeout and try different methods
  setTimeout(() => {
    console.log("Trying scroll after timeout");
    // Method 1: Using scrollTo with smooth behavior
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Method 2: Direct assignment to scrollTop
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0; // For Safari
  }, 100);
});
</script>

<style lang="scss" scoped>
.container {
  @apply w-full max-w-[1430px] mx-auto px-4;
}
</style>
